import { defineStore } from "pinia";
import {
  createKpiDraft as createKpiDraftApi,
  getKpi as getKpiApi,
  getKpiList as getKpiListApi,
  editKpiDraft as editKpiDraftApi,
  activateKpi as activateKpiApi,
  deleteKpiDraft as deleteKpiDraftApi,
  createKpiPendingDraft as createKpiPendingDraftApi,
  editKpiPendingDraft as editKpiPendingDraftApi,
  publishKpiDraft as publishKpiDraftApi,
  discardKpiPendingDraft as discardKpiPendingDraftApi,
  listKpis,
  renameKpi,
  retireKpi,
  reactivateKpi,
  archiveKpi,
  unarchiveKpi,
  unarchiveAndRenameKpi,
  getOperatingCurrency,
} from "@/api";
import { mapKpiListRow } from "@/utils/kpiCatalogUtils";

/**
 * KPI schema-default configuration (ER-8057 "Add KPI Draft").
 *
 * A name-only draft is created with these defaults so the KPI Form Draft view
 * can then edit them. Mapping to the backend create-draft contract
 * (performance/serializers/kpi.py KPICreateDraftSerializer):
 *
 *   decimal_precision  -> 0
 *   default_aggregation -> "Last"      (was "aggregation_method" in the old spec)
 *   default_target_type -> "Fixed"     (was "target_type" in the old spec)
 *   perspective        -> null        (optional post-MVP, Rule 7 / ER-12460)
 *
 * "reporting_frequency" from the original spec no longer exists in the
 * ER-11093 schema, so it is intentionally omitted. The locked fields
 * (format / favorability / operating_currency / unit_of_measure) are also
 * defaulted here: PERCENT requires no currency and no unit, which keeps a
 * name-only draft coherent under KPIs Catalog Rule 3.
 */
export const KPI_SCHEMA_DEFAULTS = Object.freeze({
  format: "PERCENT",
  favorability: "MORE_IS_FAVORABLE",
  perspective: null,
  decimal_precision: 0,
  default_target_type: "Fixed",
  default_aggregation: "Last",
  operating_currency: null,
  unit_of_measure: null,
  description: "",
});

/** User-facing error messages mandated by the ER-8057 acceptance criteria. */
export const KPI_ERROR_MESSAGES = Object.freeze({
  NAME_NOT_UNIQUE: "KPI name is not unique",
  NO_CONTENT: "There is not content to create the KPI",
});

/**
 * Build the create-draft request body from a name + configuration content.
 * Schema defaults fill every field the user has not yet configured.
 *
 * @param {{accountId: string|number, name: string, content: Object}} args
 * @returns {Object} Payload accepted by POST api/performance/v2/kpi/
 */
export function buildCreateDraftPayload({ accountId, name, content = {} }) {
  return {
    account: accountId,
    name,
    format: content.format ?? KPI_SCHEMA_DEFAULTS.format,
    favorability: content.favorability ?? KPI_SCHEMA_DEFAULTS.favorability,
    operating_currency: content.operating_currency ?? KPI_SCHEMA_DEFAULTS.operating_currency,
    unit_of_measure: content.unit_of_measure ?? KPI_SCHEMA_DEFAULTS.unit_of_measure,
    perspective: content.perspective ?? KPI_SCHEMA_DEFAULTS.perspective,
    decimal_precision: content.decimal_precision ?? KPI_SCHEMA_DEFAULTS.decimal_precision,
    default_target_type: content.default_target_type ?? KPI_SCHEMA_DEFAULTS.default_target_type,
    default_aggregation: content.default_aggregation ?? KPI_SCHEMA_DEFAULTS.default_aggregation,
    description: content.description ?? KPI_SCHEMA_DEFAULTS.description,
  };
}

/** Rule 11 row operations the catalog list can run, keyed by the KPILineMenu action. */
const LIFECYCLE_CALLS = Object.freeze({
  rename: ({ id, newName }) => renameKpi(id, newName),
  retire: ({ id }) => retireKpi(id),
  reactivate: ({ id }) => reactivateKpi(id),
  archive: ({ id }) => archiveKpi(id),
  unarchive: ({ id }) => unarchiveKpi(id),
  "unarchive-and-rename": ({ id, newName }) => unarchiveAndRenameKpi(id, newName),
});

const CURRENCY_FILTER_PAGE_SIZE = 100;

/** True when the backend rejected the create as a duplicate (Rule 10). */
function isDuplicateIdentityError(error) {
  const data = error?.response?.data || {};
  if (data.code === "KPI_DUPLICATE_IDENTITY") {
    return true;
  }
  if (typeof data.detail === "string" && /already exists|not unique/i.test(data.detail)) {
    return true;
  }
  return false;
}

/** Stored status -> label shown to users (Published is shown as "Active"). */
const KPI_STATUS_LABELS = Object.freeze({
  Draft: "Draft",
  Published: "Active",
  Retired: "Retired",
  Archived: "Archived",
});

const hasValue = (value) => value !== undefined && value !== null;

/** Editable-field payload (edit-draft / pending-draft contract) from form settings. */
export function buildEditablePayload(settings = {}) {
  return {
    decimal_precision: Number(settings.decimalPrecision) || 0,
    default_target_type: settings.targetsType,
    default_aggregation: settings.actualCalculation,
    description: settings.description ?? "",
  };
}

/**
 * Normalise a KPI into the shape the KPI form works with.
 *
 * @param {Object} raw Raw KPI row (GET kpi/<id>/): identity plus draft_* staging.
 * @param {Object} row Enriched list row (KPIListSerializer): effective values,
 *   resolved operating currency, version_number, has_pending_changes.
 *   Lifecycle responses return only this shape, so `pending` is null for them.
 */
export function normalizeKpi(raw = {}, row = {}) {
  const merged = { ...raw, ...row };
  const status = KPI_STATUS_LABELS[merged.status] ?? merged.status;
  const isDraft = status === "Draft";
  const rawPending = !isDraft && hasValue(raw.draft_decimal_precision);
  const settings = {
    decimalPrecision: merged.decimal_precision ?? raw.draft_decimal_precision ?? 0,
    targetsType: merged.default_target_type ?? raw.draft_default_target_type ?? "Fixed",
    actualCalculation: merged.default_aggregation ?? raw.draft_default_aggregation ?? "Last",
    description: merged.description ?? raw.draft_description ?? "",
  };
  return {
    id: merged.id,
    name: merged.name,
    status,
    versionNumber: merged.version_number ?? null,
    hasPendingChanges: Boolean(merged.has_pending_changes ?? rawPending),
    format: String(merged.format || "").toUpperCase(),
    unitOfMeasure: merged.unit_of_measure || "",
    operatingCurrency:
      row.operating_currency && typeof row.operating_currency === "object"
        ? row.operating_currency
        : null,
    lessIsFavorable: merged.favorability === "LESS_IS_FAVORABLE",
    settings,
    pending: rawPending
      ? {
          decimalPrecision: raw.draft_decimal_precision,
          targetsType: raw.draft_default_target_type,
          actualCalculation: raw.draft_default_aggregation,
          description: raw.draft_description ?? "",
        }
      : null,
  };
}

export const useKpisStore = defineStore("kpis", {
  state: () => ({
    kpis: [],
    totalCount: 0,
    loading: false,
    currencies: [],
  }),

  getters: {
    getKpis: (state) => state.kpis,
  },

  actions: {
    /**
     * Load one page of the KPI catalog list (Rule 12) into `kpis` / `totalCount`.
     *
     * @param {Object} params Query built by buildKpiListParams.
     */
    async fetchKpis(params) {
      this.loading = true;
      try {
        const response = await listKpis(params);
        const results = response?.data?.results ?? [];
        this.kpis = results.map(mapKpiListRow);
        this.totalCount = response?.data?.count ?? results.length;
      } catch (error) {
        this.kpis = [];
        this.totalCount = 0;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /** Currencies offered by the Operating Currency filter; a failure leaves the filter empty. */
    async fetchOperatingCurrencies(accountId) {
      try {
        const response = await getOperatingCurrency(accountId, {
          offset: 0,
          size: CURRENCY_FILTER_PAGE_SIZE,
          status: ["active", "retired"],
        });
        this.currencies = (response?.data?.results ?? [])
          .map((row) => ({ id: row.id, code: row.currency_code ?? row.code ?? row.currency?.code }))
          .filter((currency) => currency.id && currency.code);
      } catch {
        this.currencies = [];
      }
    },

    /** Run a Rule 11 row operation; rejects with the axios error so the caller can show the server message. */
    async runLifecycleAction({ action, id, newName }) {
      const call = LIFECYCLE_CALLS[action];
      if (!call) {
        throw new Error(`Unsupported KPI action: ${action}`);
      }
      const response = await call({ id, newName });
      return response?.data;
    },

    /**
     * create_kpi_draft(name, content) — ER-8057 add-draft entry point.
     *
     * @param {{accountId: string|number, name: string, content: Object}} args
     *   `content` is the configuration object; null/undefined/empty means there
     *   is nothing to create the KPI from (mapped to the specified error).
     * @returns {Promise<Object>} The created KPI row (status DRAFT).
     */
    async createKpiDraft({ accountId, name, content }) {
      if (content == null) {
        throw new Error(KPI_ERROR_MESSAGES.NO_CONTENT);
      }
      const payload = buildCreateDraftPayload({ accountId, name, content });
      try {
        const response = await createKpiDraftApi(accountId, payload);
        return response.data;
      } catch (error) {
        if (isDuplicateIdentityError(error)) {
          throw new Error(KPI_ERROR_MESSAGES.NAME_NOT_UNIQUE);
        }
        throw error;
      }
    },

    /**
     * Load a KPI for the KPI form.
     *
     * The backend has no enriched single-KPI endpoint, so the raw row
     * (name, status, draft_* staging) is combined with the matching row of the
     * enriched catalog list (version, effective values, operating currency).
     *
     * @param {string|number} kpiId
     * @returns {Promise<Object>} Normalised KPI (see normalizeKpi).
     */
    async loadKpi(kpiId) {
      const { data: raw } = await getKpiApi(kpiId);
      const list = await getKpiListApi({ search: raw.name, page_size: 100 }).catch(() => null);
      const row = (list?.data?.results || []).find((item) => String(item.id) === String(raw.id));
      return normalizeKpi(raw, row);
    },

    /** edit-draft: persist the editable fields of a Draft KPI. */
    async saveKpiDraft(kpiId, settings) {
      const response = await editKpiDraftApi(kpiId, buildEditablePayload(settings));
      return normalizeKpi({}, response.data);
    },

    /** activate: publish a Draft KPI (becomes Active, version 1). */
    async activateKpi(kpiId) {
      const response = await activateKpiApi(kpiId);
      return normalizeKpi({}, response.data);
    },

    /** delete-draft: irreversibly remove a never-activated Draft KPI. */
    async deleteKpiDraft(kpiId) {
      await deleteKpiDraftApi(kpiId);
    },

    /** create-pending-draft: start a pending draft seeded from the live version. */
    async createPendingDraft(kpiId) {
      const response = await createKpiPendingDraftApi(kpiId);
      return normalizeKpi({}, response.data);
    },

    /** edit-pending-draft: persist the staged values of the pending draft. */
    async savePendingDraft(kpiId, settings) {
      const response = await editKpiPendingDraftApi(kpiId, buildEditablePayload(settings));
      return normalizeKpi({}, response.data);
    },

    /** publish-draft: publish the pending draft as the next version. */
    async publishPendingDraft(kpiId) {
      const response = await publishKpiDraftApi(kpiId);
      return normalizeKpi({}, response.data);
    },

    /** discard-pending-draft: drop the pending draft. */
    async discardPendingDraft(kpiId) {
      const response = await discardKpiPendingDraftApi(kpiId);
      return normalizeKpi({}, response.data);
    },
  },
});
